/* ==========================================================================
   BUD TO CUP — homepage behaviour
   --------------------------------------------------------------------------
   Loaded `defer` from layout/theme.liquid, gated on template == 'index'.

   HARD CONSTRAINTS (each one is here because it broke something before):

   * ZERO jQuery, and never inline. vendor.js is deferred in <head> and
     theme.js is deferred at end-of-body; two commits (f2a3785, 0f86808) exist
     solely to fix "$ is not defined" thrown by inline section scripts racing
     them. Nothing in this file touches $ or halo.* at evaluation time. The only
     contract with the theme is ONE document event: 'cart-update'.

   * Do NOT port the prototype's whole-page scroll pass. One shared
     IntersectionObserver gates one shared rAF loop that iterates only the
     sections currently in/near the viewport. will-change goes on at activation
     and comes OFF at exit — a permanently promoted layer on four full-bleed
     images costs real GPU memory on low-end Android.

   * Geometry is cached at register + debounced resize. Reading
     getBoundingClientRect() inside the loop is a forced synchronous layout
     every frame, which is the single biggest cost in code like this.

   * transform and opacity ONLY. No top/height/filter/background-position.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ======================================================================
     Reveal — scroll entrances
     ====================================================================== */
  var Reveal = {
    io: null,
    pending: [],
    sweepBound: false,
    sweepTicking: false,
    lastSweepY: 0,

    // animate=false suppresses the transition, for elements the user never saw
    // in their pre-reveal state.
    show: function (el, animate) {
      if (el.classList.contains('is-revealed')) return;
      if (animate) {
        var delay = el.getAttribute('data-reveal-delay');
        if (delay) el.style.transitionDelay = delay + 'ms';
      } else {
        el.classList.add('btc-no-transition');
      }
      el.classList.add('is-revealed');
      if (this.io) this.io.unobserve(el);
      var i = this.pending.indexOf(el);
      if (i > -1) this.pending.splice(i, 1);
    },

    // IntersectionObserver only reports CHANGES in intersection state. A jump
    // scroll — anchor link, browser scroll restoration, bfcache, or a
    // programmatic scrollTo — moves an element from below the viewport to above
    // it in a single step. It is "not intersecting" before and after, so no
    // callback ever fires and the element stays at opacity 0 forever.
    // Measured: jumping to the page bottom stranded 45 of 46 elements invisible.
    //
    // This sweep only runs when the scroll delta exceeds half a viewport, so
    // ordinary scrolling costs nothing and the observer stays in charge of it.
    // It unbinds itself once nothing is left pending.
    bindSweep: function () {
      if (this.sweepBound) return;
      this.sweepBound = true;
      var self = this;
      this.lastSweepY = window.pageYOffset || 0;

      // Deliberately NOT rAF-throttled. The gate is a single pageYOffset read,
      // which is cheap and never triggers layout; the expensive pass only runs
      // on a genuine jump, which is rare. Deferring to rAF would also silently
      // disable this whenever the browser suspends animation frames (hidden or
      // throttled tabs), which is exactly when a restored scroll position can
      // strand content invisible.
      this.onSweep = function () {
        var y = window.pageYOffset || 0;
        var jumped = Math.abs(y - self.lastSweepY) > window.innerHeight * 0.5;
        self.lastSweepY = y;
        if (!jumped) return;
        self.sweep();
      };
      window.addEventListener('scroll', this.onSweep, { passive: true });
      window.addEventListener('pageshow', this.onSweep);
    },

    sweep: function () {
      var self = this;
      this.pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().bottom <= 0) self.show(el, false);
      });
      if (!this.pending.length) this.unbindSweep();
    },

    unbindSweep: function () {
      if (!this.sweepBound) return;
      window.removeEventListener('scroll', this.onSweep);
      window.removeEventListener('pageshow', this.onSweep);
      this.sweepBound = false;
    },

    ensureObserver: function () {
      if (this.io || typeof IntersectionObserver === 'undefined') return;
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.show(entry.target, true);
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    },

    scan: function (root) {
      var els = Array.prototype.slice.call(
        (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)')
      );
      if (!els.length) return;

      if (reduceMQ.matches || typeof IntersectionObserver === 'undefined') {
        this.revealAll(root);
        return;
      }

      this.ensureObserver();

      // Batch every READ before any WRITE, so this never thrashes layout.
      var vh = window.innerHeight;
      var tops = els.map(function (el) { return el.getBoundingClientRect().top; });

      var self = this;
      els.forEach(function (el, i) {
        // Anything already on screen at boot must appear immediately. Animating
        // it in from an offset the user never saw is worse than not animating,
        // and the hero copy is always in this set.
        if (tops[i] < vh) {
          el.classList.add('btc-no-transition', 'is-revealed');
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { el.classList.remove('btc-no-transition'); });
          });
          return;
        }
        self.pending.push(el);
        self.io.observe(el);
      });

      if (this.pending.length) this.bindSweep();
    },

    unscan: function (root) {
      if (!this.io || !root) return;
      var self = this;
      Array.prototype.forEach.call(root.querySelectorAll('[data-reveal]'), function (el) {
        self.io.unobserve(el);
      });
    },

    // The safety net. Called on reduced-motion, on missing IO support, and from
    // boot()'s catch — the resting state is CSS, so if this file dies without
    // running, below-fold content would stay invisible.
    revealAll: function (root) {
      Array.prototype.forEach.call(
        (root || document).querySelectorAll('[data-reveal]'),
        function (el) {
          el.style.transitionDelay = '';
          el.classList.add('btc-no-transition', 'is-revealed');
        }
      );
    }
  };

  /* ======================================================================
     Motion — one IO + one rAF loop for every animated scene
     ====================================================================== */
  var Motion = {
    handlers: [],
    active: [],
    io: null,
    rafId: null,
    lastY: null,
    resizeTimer: null,
    bound: false,

    writers: {
      // Hero: slow push-in over the first 700px of scroll.
      hero: function (h, y) {
        var p = Math.min(y / 700, 1);
        h.el.style.transform = 'scale(' + (1 + 0.035 * p).toFixed(4) + ')';
      },

      // Photo bands: the media box is oversized by `amount` on each side in CSS
      // (height: calc(100% + 2*amount); top: -amount) so the edge never shows.
      parallax: function (h, y, vh) {
        var centre = h.top + h.height / 2;
        var p = (y + vh / 2 - centre) / (vh + h.height);
        p = Math.max(-0.5, Math.min(0.5, p));
        h.el.style.transform = 'translate3d(0,' + (p * h.amount * 2).toFixed(1) + 'px,0)';
      },

      // Build-a-box: the four packs drift toward the box and level out.
      converge: function (h, y, vh) {
        var rectTop = h.top - y;
        var p = (vh * 0.85 - rectTop) / (vh * 0.6);
        p = Math.max(0, Math.min(1, p));
        h.parts.forEach(function (part) {
          var inv = 1 - p;
          part.el.style.transform =
            'translate3d(' + (inv * part.x).toFixed(1) + 'px,' + (inv * part.y).toFixed(1) + 'px,0)' +
            ' rotate(' + (inv * part.r).toFixed(2) + 'deg)';
        });
      }
    },

    register: function (root) {
      var kind = root.getAttribute('data-btc-motion');
      if (!this.writers[kind]) return;

      var target = root.querySelector('[data-btc-motion-target]') || root;
      var h = {
        root: root,
        el: target,
        kind: kind,
        amount: parseFloat(root.getAttribute('data-btc-parallax')) || 40,
        top: 0,
        height: 0,
        parts: []
      };

      if (kind === 'converge') {
        h.parts = Array.prototype.map.call(
          root.querySelectorAll('[data-btc-converge]'),
          function (el) {
            var v = (el.getAttribute('data-btc-converge') || '0,0,0').split(',');
            return { el: el, x: parseFloat(v[0]) || 0, y: parseFloat(v[1]) || 0, r: parseFloat(v[2]) || 0 };
          }
        );
        // A box with no packs configured yet has nothing to animate.
        if (!h.parts.length) return;
      }

      this.measure(h);
      this.handlers.push(h);
      if (this.io) this.io.observe(root);
    },

    measure: function (h) {
      var r = h.root.getBoundingClientRect();
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      h.top = r.top + y;
      h.height = r.height;
    },

    remeasure: function () {
      var self = this;
      this.handlers.forEach(function (h) { self.measure(h); });
      this.lastY = null; // force one write with the new geometry
      if (this.active.length && this.rafId === null) this.start();
    },

    init: function () {
      if (reduceMQ.matches || typeof IntersectionObserver === 'undefined') return;

      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var h = self.find(entry.target);
          if (!h) return;
          if (entry.isIntersecting) self.activate(h);
          else self.deactivate(h);
        });
      }, { rootMargin: '20% 0px 20% 0px', threshold: 0 });

      this.handlers.forEach(function (h) { self.io.observe(h.root); });

      if (!this.bound) {
        this.bound = true;
        window.addEventListener('resize', function () { self.debouncedRemeasure(); }, { passive: true });
        window.addEventListener('orientationchange', function () { self.debouncedRemeasure(); }, { passive: true });
      }
    },

    debouncedRemeasure: function () {
      var self = this;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(function () { self.remeasure(); }, 150);
    },

    find: function (el) {
      for (var i = 0; i < this.handlers.length; i++) {
        if (this.handlers[i].root === el) return this.handlers[i];
      }
      return null;
    },

    activate: function (h) {
      if (this.active.indexOf(h) !== -1) return;
      h.el.style.willChange = 'transform';
      if (h.parts.length) h.parts.forEach(function (p) { p.el.style.willChange = 'transform'; });
      this.active.push(h);
      this.start();
    },

    deactivate: function (h) {
      var i = this.active.indexOf(h);
      if (i === -1) return;
      this.active.splice(i, 1);
      // Settle to the resting transform once, then drop the layer promotion.
      this.write(h, window.pageYOffset || 0, window.innerHeight);
      h.el.style.willChange = '';
      if (h.parts.length) h.parts.forEach(function (p) { p.el.style.willChange = ''; });
      if (!this.active.length) this.stop();
    },

    write: function (h, y, vh) {
      this.writers[h.kind](h, y, vh);
    },

    start: function () {
      if (this.rafId !== null) return;
      var self = this;
      var tick = function () {
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (y !== self.lastY) {
          var vh = window.innerHeight;
          for (var i = 0; i < self.active.length; i++) self.write(self.active[i], y, vh);
          self.lastY = y;
        }
        self.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    },

    stop: function () {
      if (this.rafId === null) return;
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
      this.lastY = null;
    },

    unregister: function (root) {
      var self = this;
      this.handlers.slice().forEach(function (h) {
        if (root !== h.root && !root.contains(h.root)) return;
        self.deactivate(h);
        if (self.io) self.io.unobserve(h.root);
        self.handlers.splice(self.handlers.indexOf(h), 1);
      });
    },

    scan: function (root) {
      var self = this;
      Array.prototype.forEach.call(
        (root || document).querySelectorAll('[data-btc-motion]'),
        function (el) { if (!self.find(el)) self.register(el); }
      );
    },

    // prefers-reduced-motion can be toggled mid-session; honour it immediately.
    teardown: function () {
      var self = this;
      this.active.slice().forEach(function (h) { self.deactivate(h); });
      this.handlers.forEach(function (h) {
        h.el.style.transform = '';
        h.el.style.willChange = '';
        h.parts.forEach(function (p) { p.el.style.transform = ''; p.el.style.willChange = ''; });
      });
      if (this.io) { this.io.disconnect(); this.io = null; }
      this.stop();
    }
  };

  /* ======================================================================
     <btc-carousel> — scroll-snap, no slider library
     ====================================================================== */
  if (typeof window.customElements !== 'undefined' && typeof HTMLElement !== 'undefined') {
    var BtcCarousel = function () {
      return Reflect.construct(HTMLElement, [], BtcCarousel);
    };
    BtcCarousel.prototype = Object.create(HTMLElement.prototype);
    BtcCarousel.prototype.constructor = BtcCarousel;
    Object.setPrototypeOf(BtcCarousel, HTMLElement);

    BtcCarousel.prototype.connectedCallback = function () {
      if (this._ready) return;
      this._ready = true;

      this.track = this.querySelector('[data-btc-track]');
      this.prevBtn = this.querySelector('[data-btc-prev]');
      this.nextBtn = this.querySelector('[data-btc-next]');
      this.status = this.querySelector('[data-btc-status]');
      if (!this.track) return;

      var self = this;

      // Controls ship hidden in the markup, so a failed upgrade never leaves
      // dead buttons in the tab order.
      if (this.prevBtn) {
        this.prevBtn.hidden = false;
        this.prevBtn.addEventListener('click', function () { self.step(-1); });
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = false;
        this.nextBtn.addEventListener('click', function () { self.step(1); });
      }

      this.track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); }
      });

      this._ticking = false;
      this.track.addEventListener('scroll', function () {
        if (self._ticking) return;
        self._ticking = true;
        requestAnimationFrame(function () {
          self._ticking = false;
          self.syncControls();
        });
      }, { passive: true });

      this._onResize = function () { self.syncControls(); };
      window.addEventListener('resize', this._onResize, { passive: true });

      // btc-home.css is loaded ASYNC (media="print" + onload swap), but this
      // script is only `defer` — so the element upgrades BEFORE the slides get
      // their flex-basis. Measuring here alone sees a track that does not yet
      // overflow and hides the arrows permanently.
      //
      // A ResizeObserver on the track AND its first slide catches the moment the
      // stylesheet lands (the slide's width changes), plus any later reflow from
      // images decoding or fonts swapping. window.load is the belt-and-braces
      // fallback for engines without ResizeObserver.
      if (typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(function () { self.syncControls(); });
        this._ro.observe(this.track);
        var first = this.track.querySelector('[data-btc-slide]');
        if (first) this._ro.observe(first);
      }
      this._onLoad = function () { self.syncControls(); };
      window.addEventListener('load', this._onLoad);

      this.syncControls();
    };

    BtcCarousel.prototype.disconnectedCallback = function () {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this._onLoad) window.removeEventListener('load', this._onLoad);
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
    };

    BtcCarousel.prototype.slides = function () {
      return Array.prototype.slice.call(this.track.querySelectorAll('[data-btc-slide]'));
    };

    // Seek by live geometry rather than scrollBy(cardWidth): fractional widths
    // and gaps would otherwise accumulate drift over repeated presses. CSS
    // scroll-snap settles the exact landing point.
    BtcCarousel.prototype.step = function (dir) {
      var slides = this.slides();
      if (!slides.length) return;

      var pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      var origin = this.track.getBoundingClientRect().left + pad;
      var target = null;

      if (dir > 0) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getBoundingClientRect().left > origin + 1) { target = slides[i]; break; }
        }
      } else {
        for (var j = slides.length - 1; j >= 0; j--) {
          if (slides[j].getBoundingClientRect().left < origin - 1) { target = slides[j]; break; }
        }
      }
      if (!target) return;

      this.track.scrollBy({
        left: target.getBoundingClientRect().left - origin,
        behavior: reduceMQ.matches ? 'auto' : 'smooth'
      });

      // Announced only on button press. Doing it on scroll would spam a screen
      // reader on every swipe frame.
      if (this.status) {
        this.status.textContent = 'Slide ' + (slides.indexOf(target) + 1) + ' of ' + slides.length;
      }
    };

    BtcCarousel.prototype.syncControls = function () {
      var t = this.track;
      // scrollLeft is negative under dir="rtl" in some engines.
      var pos = Math.abs(t.scrollLeft);
      var max = t.scrollWidth - t.clientWidth;
      var overflows = max > 1;

      if (this.prevBtn) {
        this.prevBtn.hidden = !overflows;
        this.prevBtn.disabled = pos <= 1;
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = !overflows;
        this.nextBtn.disabled = pos >= max - 1;
      }
    };

    if (!window.customElements.get('btc-carousel')) {
      window.customElements.define('btc-carousel', BtcCarousel);
    }
  }

  /* ======================================================================
     AddedState — the "ADDED ✓" confirmation on Scene 05
     ----------------------------------------------------------------------
     The theme owns the actual add: theme.js:3048 delegates
     [data-btn-addtocart] on document, and because our button is NOT inside a
     <product-form>, it takes actionAddToCart -> Shopify.addItem ->
     after_add_to_cart == 'quick_cart' -> updateSidebarCart, which dispatches
     'cart-update' on document at theme.js:3739. We add no cart code.

     'cart-update' carries no reference to the button that caused it, so we
     track the pending button ourselves.
     ====================================================================== */
  var AddedState = {
    pending: null,
    timer: null,
    WINDOW: 6000,
    HOLD: 2500,

    init: function () {
      var self = this;

      // CAPTURE phase. theme.js:3051 calls stopPropagation() during BUBBLING at
      // document, which can never suppress a capture listener on an ancestor —
      // so this is the only phase that reliably sees the click.
      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('[data-btn-addtocart]');
        if (!btn) return;
        if (!btn.closest('[data-btc-atc-scope]')) return;
        // Quick-shop and notify-me open a popup/form; neither adds to the cart.
        if (btn.hasAttribute('data-quickshop-popup')) return;
        if (btn.classList.contains('is-notify-me')) return;

        self.setState(btn, 'adding');
        self.pending = btn;
        clearTimeout(self.timer);
        // The error path (Shopify.onError) fires no event at all, so without
        // this the button would spin forever.
        self.timer = setTimeout(function () { self.clear(); }, self.WINDOW);
      }, true);

      document.addEventListener('cart-update', function () {
        if (!self.pending) return;
        var btn = self.pending;
        self.pending = null;
        clearTimeout(self.timer);

        self.setState(btn, 'added');
        var card = btn.closest('[data-btc-card]');
        var live = card && card.querySelector('[data-btc-card-status]');
        if (live) live.textContent = (card.getAttribute('data-btc-title') || 'Item') + ' added to cart';

        self.timer = setTimeout(function () {
          self.setState(btn, '');
          if (live) live.textContent = '';
        }, self.HOLD);
      });
    },

    // Never touches the button's text node: theme.js:3101 caches
    // originalMessage from it for .button-text-change buttons, and rewriting it
    // corrupts that bookkeeping. The label swap is CSS on a sibling <span>.
    //
    // The state is mirrored onto the [data-btc-atc] wrapper because that sibling
    // span lives outside the theme's <form>, and CSS cannot select an ancestor
    // of the button to reach it.
    setState: function (btn, state) {
      var wrap = btn.closest('[data-btc-atc]');
      if (state) {
        btn.setAttribute('data-btc-state', state);
        if (wrap) wrap.setAttribute('data-btc-state', state);
      } else {
        btn.removeAttribute('data-btc-state');
        if (wrap) wrap.removeAttribute('data-btc-state');
      }
    },

    clear: function () {
      if (!this.pending) return;
      this.setState(this.pending, '');
      // theme.js:3050 does $(event.target), so is-loading may have landed on an
      // inner span rather than the button itself.
      this.pending.classList.remove('is-loading');
      Array.prototype.forEach.call(this.pending.querySelectorAll('.is-loading'), function (el) {
        el.classList.remove('is-loading');
      });
      this.pending = null;
    }
  };

  /* ======================================================================
     NewsletterForm — Scene 10
     ----------------------------------------------------------------------
     snippets/block-newsletter-footer.liquid already runs {% form 'customer' %}
     in the footer of every page. form.posted_successfully? is per-REQUEST, not
     per-form, so a native submit would render BOTH confirmations. Posting over
     fetch keeps the success state local to this section — and is what makes its
     aria-live region meaningful.

     Progressive enhancement: if fetch is missing we never preventDefault, so the
     native POST still subscribes the visitor.
     ====================================================================== */
  var NewsletterForm = {
    init: function () {
      if (typeof fetch === 'undefined' || typeof FormData === 'undefined') return;

      Array.prototype.forEach.call(document.querySelectorAll('[data-btc-newsletter]'), function (root) {
        var form = root.querySelector('form');
        var success = root.querySelector('[data-btc-newsletter-success]');
        if (!form || !success) return;

        form.addEventListener('submit', function (e) {
          // Let the browser show its own validation UI rather than posting junk.
          if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

          e.preventDefault();
          var btn = form.querySelector('[type="submit"]');
          if (btn) btn.disabled = true;

          fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { 'Accept': 'text/html' }
          })
            .then(function (res) {
              // Shopify redirects to ?customer_posted=true on success.
              var ok = res.redirected || res.url.indexOf('customer_posted=true') > -1 || res.ok;
              if (!ok) throw new Error('subscribe failed');
              form.hidden = true;
              success.hidden = false;
            })
            .catch(function () {
              // Fall back to a real submit so the visitor is never stranded.
              if (btn) btn.disabled = false;
              form.submit();
            });
        });
      });
    }
  };

  /* ======================================================================
     Track — homepage engagement events
     ----------------------------------------------------------------------
     THESE ARE NOT ECOMMERCE EVENTS, and that is the whole design constraint.

     Shopify custom pixel 204833067 owns every GA4 ecommerce event on this
     store — view_item, add_to_cart, purchase, the lot. A second push of any of
     those double-counts, which ran undetected in production until 2026-08-03.
     snippets/ga4-datalayer.liquid owns exactly two more (select_item and
     view_search_results) because the pixel structurally cannot send them.

     Everything here is a btc_-prefixed engagement event that neither side
     sends, so there is no overlap to double-count. Nothing in this module
     writes an `ecommerce` key — that keeps these events outside the
     `dataLayer.push({ecommerce: null})` reset convention entirely, so they can
     never interfere with the pixel's item payloads.

     ⚠ NOT YET VISIBLE IN GA4. GTM-W93RS2RB's only event tag (Tag 8) triggers
     on the regex `select_item|view_search_results`. These five events reach the
     dataLayer and fire NO tag until someone adds a `btc_.*` Custom Event
     trigger in GTM. That is deliberate: shipping the instrumentation and the
     container change together would have meant editing GTM from a task that is
     preview-only. The events are inert, not broken.

     No new library, no script tag, no window.fetch patch. One delegated click
     listener and one IntersectionObserver.
     ====================================================================== */
  var Track = {
    push: function (event, data) {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (data) {
        for (var k in data) {
          if (Object.prototype.hasOwnProperty.call(data, k)) payload[k] = data[k];
        }
      }
      window.dataLayer.push(payload);
    },

    init: function () {
      var self = this;

      // Capture phase, matching ga4-datalayer.liquid: the push is synchronous,
      // so it always completes before the browser starts navigating.
      document.addEventListener('click', function (e) {
        var el = e.target.closest ? e.target.closest('[data-btc-track]') : null;
        if (!el) return;

        var event = el.getAttribute('data-btc-track');
        if (!event) return;

        self.push(event, {
          btc_label: (el.textContent || '').trim().slice(0, 80),
          btc_destination: el.getAttribute('href') || ''
        });
      }, true);

      this.observeBrewScene();
    },

    // Fires once per page when the hot/iced scene is genuinely on screen, so
    // the CTA click-through below it has a denominator. Independent of Motion:
    // a visitor who asks for reduced motion is still a visitor who saw it.
    observeBrewScene: function () {
      var scene = document.querySelector('[data-btc-brew-scene]');
      if (!scene || typeof IntersectionObserver === 'undefined') return;

      var self = this;
      var fired = false;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (fired || !entry.isIntersecting) return;
          fired = true;
          io.disconnect();
          self.push('btc_brew_method_exposure', {
            btc_layout: scene.getAttribute('data-btc-brew-scene') || ''
          });
        });
      }, { threshold: 0.5 });

      io.observe(scene);
    }
  };

  /* ======================================================================
     Boot
     ====================================================================== */
  function boot() {
    Reveal.scan(document);
    Motion.scan(document);
    Motion.init();
    AddedState.init();
    NewsletterForm.init();
    Track.init();

    // The theme editor re-renders one section at a time. Without this,
    // animation silently dies while the merchant is editing — and they report
    // it as broken.
    document.addEventListener('shopify:section:load', function (e) {
      Motion.scan(e.target);
      Motion.remeasure();
      if (Motion.io) {
        Array.prototype.forEach.call(e.target.querySelectorAll('[data-btc-motion]'), function (el) {
          var h = Motion.find(el);
          if (h) Motion.io.observe(el);
        });
      }
      Reveal.scan(e.target);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      Motion.unregister(e.target);
      Reveal.unscan(e.target);
    });

    var onMotionPrefChange = function () {
      if (reduceMQ.matches) {
        Motion.teardown();
        Reveal.revealAll(document);
      } else {
        Motion.scan(document);
        Motion.init();
      }
    };
    if (reduceMQ.addEventListener) reduceMQ.addEventListener('change', onMotionPrefChange);
    else if (reduceMQ.addListener) reduceMQ.addListener(onMotionPrefChange);
  }

  try {
    boot();
  } catch (err) {
    // The [data-reveal] resting state is CSS. If boot throws before Reveal runs,
    // the whole page below the fold stays invisible — so fail open, loudly.
    Reveal.revealAll(document);
    if (window.console && console.error) console.error('[btc-home] boot failed:', err);
  }
})();
